/**
 * HomePage — The Exchange Alliance landing (recreation of hoh2life.com).
 * Everything (header, hero, program sections, partners, contact form, footer)
 * lives in this one file so it reads top-to-bottom like the live site.
 */
import React, { useState } from "react";
import { CommercialCta } from "@/components/commercial/CommercialCta";
import { TeaWordmark } from "@/components/TeaWordmark";
import { TeaLogo } from "@/components/TeaLogo";
import { Link } from "react-router-dom";
import { Editable } from "@/lib/editable";
import WatchPlatformButton from "@/components/WatchPlatformButton";
import {
  HeartHandshake, ArrowRight, Play, Heart, Users, Briefcase, Columns3, Home,
  ArrowUpRight, Building2, HandCoins, Receipt, ClipboardList, ShieldCheck,
  FileSpreadsheet, Award, BarChart3, ExternalLink, Lock, Menu, X,
} from "lucide-react";

// ------- Design tokens (copied from live site) -------
const C = {
  bgCream: "#FAFAF7",
  bgCreamAlt: "#F3F3EE",
  ink: "#0A0612",
  gold: "#D4AF37",
  goldDeep: "#C9A227",
  goldLight: "#F4D88A",
  teal: "#1E6F70",
  tealDeep: "#0F766E",
  pine: "#1B4332",
  pineMid: "#2D6A4F",
  rule: "#E8E5DA",
  mute: "#6B6F76",
};

// Scoped CSS: the metallic wordmark gradients + font stack
const PAGE_CSS = `
.tea-root { font-family: "Inter", "Helvetica Neue", Arial, sans-serif; }
.tea-display { font-family: "Oswald", "Playfair Display", "Georgia", serif; letter-spacing: -0.01em; }
.tea-wordmark { font-family: "Oswald", "Playfair Display", serif; font-weight: 700; letter-spacing: 0.005em; }
.tea-mono { font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace; }
.tea-metal { -webkit-background-clip: text; background-clip: text; color: transparent; display:inline-block; }
.tea-metal-gold { background-image: linear-gradient(180deg,#F7E39B 0%,#E7C458 35%,#B8891F 68%,#8A5F10 100%); }
.tea-metal-teal { background-image: linear-gradient(180deg,#BEE7E6 0%,#5CBFBE 35%,#1E6F70 70%,#0F3F40 100%); }
.tea-ticker-dot { transition: all .4s ease; }
`;



// ===================================================
// HEADER
// ===================================================
const DEMO_URL = "https://nexengines.com/demo/exchange-alliance/preview";
const trackDemo = (source) => navigator.sendBeacon?.(
  `${process.env.REACT_APP_BACKEND_URL}/api/track/demo-click`,
  new Blob([JSON.stringify({ source })], { type: "application/json" }));
const NAV = [
  { label: "Home", href: "#top" },
  { label: "Impact", href: "#impact" },
  { label: "Employers", href: "#employers-anchor" },
  { label: "Non-Profit Alliances", href: "#alliances-anchor" },
  { label: "Personal Advancement and Career Training", href: "#ucms-anchor" },
  { label: "Supporters", href: "#partners" },
  { label: "Live Demo", href: DEMO_URL, external: true },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="tea-header" className="sticky top-0 z-40" style={{ background: C.teal }}>
      <div className="mx-auto max-w-[1500px] px-5 lg:px-8 py-2.5 flex items-center gap-5">
        <a href="#top" className="flex items-center gap-3 shrink-0" data-testid="tea-header-brand">
          <TeaLogo size={44} testid="tea-header-logo-left" />
          <div className="leading-tight hidden sm:block">
            <div className="tea-wordmark text-[17px] md:text-[18px] tracking-tight uppercase" style={{ color: "#F5EFD6" }}>
              <TeaWordmark small text="THE EXCHANGE ALLIANCE" testid="home-header-wordmark" className="font-bold" />
            </div>
            <div className="tea-mono text-[8.5px] tracking-[0.3em] uppercase text-white/70 mt-0.5">The Last · The Least · The Lost</div>
          </div>
        </a>

        <nav className="hidden xl:flex items-center gap-7 ml-8 flex-1" aria-label="Primary">
          {NAV.map((n) => (
            <a key={n.label} href={n.href} {...(n.external ? { target: "_blank", rel: "noopener", onClick: () => trackDemo("menu") } : {})}
              data-testid={`tea-nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="tea-mono text-[10px] tracking-[0.22em] uppercase text-[#F4D88A] hover:text-white transition whitespace-nowrap">
              {n.label}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3 shrink-0">
        <Link to="/login" data-testid="tea-header-portal"
          className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-white tea-mono text-[10px] tracking-[0.22em] uppercase font-bold shadow-sm"
          style={{ color: C.ink }}>
          <Lock className="h-3.5 w-3.5" /> Portal Login
        </Link>

        <TeaLogo size={44} testid="tea-header-logo-right" />
        <button type="button" aria-label="Open menu" className="xl:hidden text-white p-2" onClick={() => setOpen((v) => !v)} data-testid="tea-header-menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-white/15 bg-[color:var(--teal,#1E6F70)] px-5 py-4 flex flex-col gap-3" style={{"--teal":C.teal}}>
          {NAV.map((n) => (
            <a key={n.label} href={n.href} {...(n.external ? { target: "_blank", rel: "noopener" } : {})} onClick={() => { if (n.external) trackDemo("menu"); setOpen(false); }} className="tea-mono text-[11px] tracking-[0.2em] uppercase text-[#F4D88A]">{n.label}</a>
          ))}
          <Link to="/login" data-testid="tea-mobile-portal" className="tea-mono text-[11px] tracking-[0.2em] uppercase text-[color:var(--gold,#D4AF37)]" style={{"--gold":C.goldLight}}>Portal Login</Link>
        </div>
      )}
    </header>
  );
};

// ===================================================
// HERO
// ===================================================
const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative min-h-[85vh] lg:min-h-[90vh] w-full overflow-hidden flex flex-col" style={{ background: C.ink }}>
    <img
      alt="A diverse team of TEA partners and program graduates collaborating in a modern workplace."
      className="absolute inset-0 h-full w-full object-cover object-center"
      loading="eager"
      src="https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=2400"
    />
    <div className="absolute inset-0 hidden md:block" style={{ background: "linear-gradient(90deg,#0A0612E6 0%,#0A061299 55%,transparent 100%)" }} />
    <div className="absolute inset-0 md:hidden" style={{ background: "linear-gradient(180deg,#0A0612E6,#0A0612CC 60%,#0A0612B3)" }} />

    <div className="relative z-10 flex-1 px-6 md:px-12 lg:px-24 max-w-[1400px] w-full mx-auto flex items-center">
      <div className="max-w-3xl py-20 lg:py-24">
        <div data-testid="hero-eyebrow" className="inline-flex items-center gap-2.5 mb-8 px-4 py-1.5 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: C.gold }} />
          <Editable keyId="home.hero.eyebrow" as="span" className="tea-mono text-[10px] tracking-[0.25em] uppercase text-white/90">Commitment to Career · Three Phases</Editable>
        </div>

        <h1 data-testid="hero-headline" className="tea-wordmark text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] uppercase sm:whitespace-nowrap">
          <TeaWordmark testid="home-hero-wordmark" />
        </h1>

        <Editable keyId="home.hero.subline" as="p" className="mt-5 tea-display text-xl md:text-2xl lg:text-3xl font-bold tracking-tight leading-snug text-white/95 block">
          Serving the Last, the Least and the Lost: HIS Mission, HIS Mandate
        </Editable>

        <Editable keyId="home.hero.scripture" as="div" className="mt-3 tea-mono text-[10px] tracking-[0.25em] uppercase text-white/60 block">
          Matthew 25:40 · Luke 19:10
        </Editable>

        <Editable keyId="home.hero.mission" as="p" className="mt-8 text-base md:text-lg text-white/75 leading-relaxed max-w-xl block">
          Our Commitment to Career is how we live out that mandate: three phases — Triage, Sanctuary, and Career — that we establish and hold the standard for, administered alongside credentialed partner agencies, so the people the world steps over are seen, housed, trained, and sent back out standing on their own feet.
        </Editable>

        <div data-testid="hero-ctas" className="mt-10 flex flex-wrap items-center gap-4">
          <a href="#assist" data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-2.5 text-white px-8 py-4 rounded-full font-semibold tracking-tight text-base transition-all shadow-lg hover:scale-[1.02]"
            style={{ background: C.pine, boxShadow: `0 10px 30px ${C.pine}4D` }}>
            <HeartHandshake className="h-5 w-5" /> Get Assistance Now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a href="#assist" data-testid="hero-secondary-cta"
            className="group inline-flex items-center gap-2.5 bg-transparent border border-white/30 hover:bg-white/10 hover:border-white/50 text-white px-8 py-4 rounded-full font-semibold tracking-tight text-base backdrop-blur-sm transition-all">
            Partner With Us · Donate <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <CommercialCta source="hero" className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-semibold tracking-tight text-base transition-all shadow-lg hover:scale-[1.02]"
            style={{ background: C.gold, color: C.ink, boxShadow: `0 10px 30px ${C.gold}4D` }} />
          <WatchPlatformButton className="group inline-flex items-center gap-2.5 bg-transparent border px-8 py-4 rounded-full font-semibold tracking-tight text-base backdrop-blur-sm transition-all hover:bg-white/10 hover:scale-[1.02] disabled:opacity-70"
            style={{ borderColor: C.gold, color: C.gold }} />
        </div>

        <div data-testid="hero-tag-row" className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 tea-mono text-[10px] tracking-[0.25em] uppercase text-white/70">
          <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5" style={{ background: C.pine }} /> Triage & Stabilization</span>
          <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5" style={{ background: C.gold }} /> Residential Sanctuary</span>
          <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5" style={{ background: C.tealDeep }} /> Career & Independence</span>
        </div>
      </div>
    </div>

    {/* Stats strip pinned to hero bottom */}
    <div data-testid="hero-stats-strip" className="relative z-10 w-full backdrop-blur-md border-t" style={{ background: `${C.teal}E6`, borderColor: `${C.gold}99` }}>
      <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto py-6 lg:py-8">
        <div className="flex flex-col md:flex-row items-stretch md:divide-x" style={{ "--divcol": `${C.gold}66` }}>
          {[
            ["159", "Residential Beds", "residential-beds"],
            ["250+", "Partner Agencies", "partner-agencies"],
            ["$45K–$55K", "Career Track Wages", "career-track"],
          ].map(([val, label, tid]) => (
            <div key={tid} data-testid={`hero-stat-${tid}`} className="flex-1 py-4 md:py-1 md:px-6 lg:px-10 flex flex-col items-start justify-center border-t md:border-t-0 md:border-l first:md:border-l-0" style={{ borderColor: `${C.gold}66` }}>
              <div className="tea-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-none">
                <span className="tea-metal tea-metal-gold">{val}</span>
              </div>
              <div className="mt-2 tea-mono text-[10px] md:text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: `${C.goldLight}D9` }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ===================================================
// IMPACT TICKER (dark bar below hero)
// ===================================================
const ImpactTicker = () => (
  <div id="impact" data-testid="impact-ticker" className="w-full border-y overflow-hidden" style={{ background: C.ink, color: C.goldLight, borderColor: `${C.goldLight}26` }}>
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24 py-3 md:py-4">
      <div className="flex items-center gap-4 md:gap-6 flex-wrap md:flex-nowrap">
        <div className="flex items-center gap-2 tea-mono text-[9px] md:text-[10px] tracking-[0.3em] uppercase shrink-0" style={{ color: `${C.goldLight}B3` }}>
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: "#34d399" }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#34d399" }} />
          </span>
          Live Pulse
        </div>
        <div data-testid="ticker-reel" className="flex items-center gap-5 md:gap-8 flex-wrap md:flex-nowrap flex-1">
          <div className="flex items-baseline gap-2 min-w-0" data-testid="ticker-cell-avoided">
            <Heart className="h-3.5 w-3.5 self-center shrink-0" style={{ color: C.goldDeep }} />
            <span className="tea-display text-xl md:text-2xl font-bold text-white tabular-nums" data-testid="ticker-value-avoided">$1,404,280</span>
            <span className="tea-mono text-[9px] md:text-[10px] tracking-[0.22em] uppercase whitespace-nowrap" style={{ color: `${C.goldLight}B3` }}>community cost avoided</span>
          </div>
          <span className="hidden md:inline" style={{ color: `${C.goldLight}4D` }}>·</span>
          <div className="flex items-baseline gap-2 min-w-0" data-testid="ticker-cell-active">
            <Users className="h-3.5 w-3.5 self-center shrink-0" style={{ color: C.goldDeep }} />
            <span className="tea-display text-xl md:text-2xl font-bold text-white tabular-nums" data-testid="ticker-value-active">24</span>
            <span className="tea-mono text-[9px] md:text-[10px] tracking-[0.22em] uppercase whitespace-nowrap" style={{ color: `${C.goldLight}B3` }}>residents active</span>
          </div>
        </div>
        <div className="flex gap-1.5 ml-auto" data-testid="ticker-dots">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} data-testid={`ticker-dot-${i}`} className="tea-ticker-dot rounded-full h-1.5" style={{ width: i === 0 ? 24 : 6, background: i === 0 ? C.goldLight : `${C.goldLight}40` }} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ===================================================
// LIVE-DATA CTA RIBBON
// ===================================================
const LiveDataCta = () => (
  <section data-testid="impact-homepage-cta" className="py-10 md:py-14 border-y text-white" style={{ background: C.ink, borderColor: `${C.gold}66` }}>
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
      <div className="flex-1">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border tea-mono text-[10px] tracking-[0.22em] uppercase mb-3" style={{ color: "#6ee7b7", borderColor: "#10b98180", background: "#10b98119" }}>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#34d399" }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#10b981" }} />
          </span>
          Live Data
        </div>
        <div className="tea-display text-2xl md:text-3xl font-bold tracking-tight leading-tight">
          Real dollars, real people housed, real hours worked — see the receipts.
        </div>
      </div>
      <a href="#impact" data-testid="homepage-impact-cta-btn"
        className="inline-flex items-center gap-2 tea-mono text-[11px] tracking-[0.18em] uppercase px-6 py-3.5 flex-shrink-0 transition-colors border"
        style={{ background: C.goldLight, color: C.ink, borderColor: `${C.gold}99` }}>
        See Our Live Impact <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  </section>
);

// ===================================================
// 3-CARD CTA GRID
// ===================================================
const EntryCta = () => (
  <section data-testid="public-entry-cta" className="py-12 md:py-16 border-y" style={{ background: "#fff", borderColor: `${C.ink}0D` }}>
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Employers card */}
        <a href="#employers-anchor" data-testid="landing-labor-cta"
          className="group rounded-2xl border-2 p-8 md:p-10 flex flex-col transition-all hover:shadow-lg hover:-translate-y-1"
          style={{ borderColor: `${C.ink}1A`, background: C.bgCream }}>
          <div className="h-12 w-12 rounded-full grid place-items-center mb-4" style={{ background: C.ink, color: C.goldLight }}>
            <Briefcase className="h-5 w-5" />
          </div>
          <div className="tea-mono text-[10px] tracking-[0.25em] uppercase mb-2" style={{ color: C.goldDeep }}>For Employers</div>
          <div className="tea-display text-2xl md:text-3xl font-bold tracking-tight mb-2" style={{ color: C.ink }}>Estimate your labor cost.</div>
          <div className="text-sm md:text-base leading-relaxed flex-1" style={{ color: `${C.ink}B3` }}>
            Vetted, program-supported workers at 25–40% below market. Slide, calc, submit.
          </div>
          <div className="mt-6 inline-flex items-center gap-2 tea-mono text-[11px] tracking-[0.22em] uppercase" style={{ color: C.ink }}>
            Open Calculator <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </a>

        {/* Four Pillars card (image bg) */}
        <a href="#four-pillars" data-testid="landing-four-pillars-cta"
          className="group relative rounded-2xl overflow-hidden border-2 p-8 md:p-10 flex flex-col transition-all hover:shadow-2xl hover:-translate-y-1 min-h-[280px]"
          style={{ borderColor: `${C.ink}1A` }}>
          <div aria-hidden className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&q=80&w=1400')" }} />
          <div aria-hidden className="absolute inset-0" style={{ background: `linear-gradient(135deg,${C.ink}D9,${C.ink}B3 55%,${C.ink}8C)` }} />
          <div className="relative flex flex-col h-full">
            <div className="h-12 w-12 rounded-full grid place-items-center mb-4 border-2" style={{ background: C.goldDeep, color: C.ink, borderColor: `${C.goldLight}80` }}>
              <Columns3 className="h-5 w-5" />
            </div>
            <div className="tea-mono text-[10px] tracking-[0.25em] uppercase mb-2" style={{ color: C.goldLight }}>Our Foundation</div>
            <div className="tea-display text-2xl md:text-3xl font-bold tracking-tight mb-2 leading-tight" style={{ color: "#F5F1E8" }}>Discover the 4 Pillars of Transformation.</div>
            <div className="text-sm md:text-base leading-relaxed flex-1" style={{ color: "#E8E5DAE6" }}>
              Explore the Case Management, Life Skills, Christ-Centered Counseling, and Daily Check-In structure driving our 24-month roadmap.
            </div>
            <div className="mt-6 inline-flex items-center gap-2 tea-mono text-[11px] tracking-[0.22em] uppercase" style={{ color: C.goldLight }}>
              Explore Our 4 Pillars <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        </a>

        {/* Intake card */}
        <a href="#assist" data-testid="landing-intake-cta"
          className="group rounded-2xl border-2 p-8 md:p-10 flex flex-col transition-all hover:shadow-lg hover:-translate-y-1"
          style={{ borderColor: `${C.ink}1A`, background: C.bgCream }}>
          <div className="h-12 w-12 rounded-full grid place-items-center mb-4" style={{ background: C.pine, color: C.goldLight }}>
            <Home className="h-5 w-5" />
          </div>
          <div className="tea-mono text-[10px] tracking-[0.25em] uppercase mb-2" style={{ color: C.pine }}>Need Support?</div>
          <div className="tea-display text-2xl md:text-3xl font-bold tracking-tight mb-2" style={{ color: C.ink }}>Ask for a seat at the table.</div>
          <div className="text-sm md:text-base leading-relaxed flex-1" style={{ color: `${C.ink}B3` }}>
            4-minute confidential intake for housing + case management + workforce support.
          </div>
          <div className="mt-6 inline-flex items-center gap-2 tea-mono text-[11px] tracking-[0.22em] uppercase" style={{ color: C.ink }}>
            Start Intake <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </a>
      </div>
    </div>
  </section>
);

// ===================================================
// ROADMAP PREVIEW (8 checkpoints)
// ===================================================
const ROADMAP_DOTS = [
  ["d30", "Day 30", "Stabilization", "82 reached"],
  ["d60", "Day 60", "Engagement", "43 reached"],
  ["d90", "Day 90", "Foundation", "24 reached"],
  ["d120", "Day 120", "Vocational Targeting", "15 reached"],
  ["d180", "Month 6", "Skills Certification Gate", "0 reached"],
  ["m12", "Month 12", "Independence Pivot", "0 reached"],
  ["m18", "Month 18", "Pre-Graduation", "0 reached"],
  ["m24", "Month 24", "Program Completion", "0 reached"],
];
const RoadmapPreview = () => (
  <section data-testid="roadmap-preview" className="py-14 md:py-20 border-y text-white" style={{ background: C.ink, borderColor: `${C.goldLight}33` }}>
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-8 md:mb-12">
        <div>
          <div className="tea-mono text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: `${C.goldLight}CC` }}>The 24-Month Roadmap</div>
          <h2 className="tea-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl">8 checkpoints. 730 days. Zero shortcuts.</h2>
        </div>
        <a href="#roadmap" data-testid="roadmap-preview-cta"
          className="inline-flex items-center gap-2 border px-5 py-3 tea-mono text-[10px] tracking-[0.22em] uppercase hover:bg-[color:var(--gl,#F4D88A)] hover:text-[color:var(--ink,#0A0612)] transition-colors"
          style={{ "--gl": C.goldLight, "--ink": C.ink, borderColor: `${C.goldLight}80`, color: C.goldLight }}>
          Explore Full Roadmap <ArrowRight className="h-4 w-4" />
        </a>
      </div>
      <div className="relative">
        <div className="hidden md:block absolute left-0 right-0 top-6 h-px bg-white/15" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 relative">
          {ROADMAP_DOTS.map(([id, when, label, count]) => (
            <a key={id} href={`#roadmap-${id}`} data-testid={`roadmap-preview-dot-${id}`} className="group flex flex-col items-center text-center">
              <div className="relative">
                <div className="h-3 w-3 rounded-full ring-4 transition-colors" style={{ background: C.goldLight, boxShadow: `0 0 0 4px ${C.ink}` }} />
              </div>
              <div className="mt-4 tea-mono text-[9px] tracking-[0.22em] uppercase" style={{ color: `${C.goldLight}B3` }}>{when}</div>
              <div className="mt-1 text-sm font-bold leading-snug group-hover:text-[color:var(--gl,#F4D88A)]" style={{ "--gl": C.goldLight }}>{label}</div>
              <div className="mt-2 tea-mono text-[10px] tracking-[0.18em] uppercase tabular-nums text-white/50">{count}</div>
            </a>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ===================================================
// CAREER DESTINATIONS (4 cards)
// ===================================================
const CAREER = [
  ["hospitality", "Hospitality", "From line cook to sous chef — restaurant, hotel, and resort placements with growth paths.", "$32K–$48K avg.", "https://images.unsplash.com/photo-1768162125977-9944219971c5?auto=format&fit=crop&w=900&q=80", "Chefs working candidly in a busy professional kitchen, plating service."],
  ["logistics", "Logistics", "Warehouse, distribution, last-mile delivery — high-throughput partners across the region.", "$38K–$52K avg.", "https://images.unsplash.com/photo-1687766011004-8cb676b03bce?auto=format&fit=crop&w=900&q=80", "Warehouse associate scanning inventory in a high-volume distribution center."],
  ["skilled_trades", "Skilled Trades", "Apprenticeship-tracked carpentry, HVAC, electrical, and plumbing pathways.", "$45K–$72K avg.", "https://images.unsplash.com/photo-1622426385889-4fc93a72423a?auto=format&fit=crop&w=900&q=80", "Construction tradesperson in PPE working on a residential build site."],
  ["healthcare", "Healthcare", "Certified Nursing Assistants, Medical Office staff, and Behavioral Health roles.", "$40K–$55K avg.", "https://images.pexels.com/photos/8413157/pexels-photo-8413157.jpeg?auto=compress&cs=tinysrgb&w=900", "Nurse in scrubs sharing a quiet moment with a patient in a clinic exam room."],
];
const CareerDestinations = () => (
  <>
    <h2 id="employers-anchor" className="sr-only">Strategic Employer Partnerships</h2>
    <section data-testid="career-destinations" className="py-24 lg:py-28" style={{ background: C.bgCream }}>
      <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="mb-14 lg:mb-18 max-w-3xl">
          <div className="tea-mono text-[10px] tracking-[0.25em] uppercase font-bold mb-4" style={{ color: C.pine }}>
            <span className="inline-flex items-center gap-2"><span className="h-1.5 w-6" style={{ background: C.pine }} />Where Our People Land</span>
          </div>
          <h2 className="tea-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" style={{ color: C.ink }}>
            Career destinations across four industries that <span style={{ color: C.pine }}>actually hire transitioning workers.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: `${C.ink}B3` }}>
            A pre-certified, drug-tested, soft-skills-trained workforce ready to step onto the floor. Not a referral list — people the world counted last, now placed with accountability behind every résumé.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {CAREER.map(([k, title, body, chip, img, alt]) => (
            <article key={k} data-testid={`career-card-${k}`} className="group bg-white rounded-2xl overflow-hidden border shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300" style={{ borderColor: `${C.ink}0D` }}>
              <div className="aspect-[4/3] overflow-hidden" style={{ background: C.bgCreamAlt }}>
                <img alt={alt} src={img} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-6 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="tea-display text-2xl font-bold tracking-tight" style={{ color: C.ink }}>{title}</h3>
                  <ArrowUpRight className="h-5 w-5 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: C.pine }} />
                </div>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: `${C.ink}B3` }}>{body}</p>
                <span data-testid={`career-wage-chip-${k}`} className="mt-5 self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs tea-mono tracking-wider font-semibold" style={{ background: `${C.pine}14`, color: C.pine }}>
                  <span className="h-1 w-1 rounded-full" style={{ background: C.pine }} />{chip}
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  </>
);

// ===================================================
// 3 PHASE CARDS (Triage / Sanctuary / Career)
// ===================================================
const PHASES = [
  {
    k: "stabilization", color: C.pine, href: "#assist", cta: "Start an Intake", flip: false,
    plate: "Phase 01 · Days 1 – 30", kicker: "OUTREACH · INTAKE · BEHAVIORAL HEALTH",
    title: "Triage & Stabilization",
    body: "We go to the Lost where they are — encampment outreach, Vibe Check intake, and a behavioral-health baseline that turns survival mode into an actual plan.",
    bullets: ["Encampment outreach and Vibe Check intake", "Mental-health, behavioral and medical baseline", "Bridge Pipeline activates Big Four ID recovery"],
    img: "https://images.pexels.com/photos/7176026/pexels-photo-7176026.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "A trauma-informed counselor leaning forward attentively.",
  },
  {
    k: "sanctuary", color: C.goldDeep, href: "#four-pillars", cta: "See Our Foundation", flip: true,
    plate: "Phase 02 · Months 2 – 12", kicker: "STABILIZATION · COMMUNITY · HOUSING",
    title: "Residential Sanctuary",
    body: "Where the Least are made whole — sober transitional housing, daily structure, and a community that holds the line between crisis and career.",
    bullets: ["159 sober transitional beds across vetted partner homes", "Daily accountability check-ins + curfewed compliance ladder", "Step-down pathway into the independent-housing roster"],
    img: "https://images.unsplash.com/photo-1609253932702-796cbf3d3171?auto=format&fit=crop&w=1200&q=80",
    alt: "A diverse community group seated together in a sunlit common area.",
  },
  {
    k: "career", color: C.tealDeep, href: "#employers-anchor", cta: "Meet Our Employers", flip: false,
    plate: "Phase 03 · Months 13 – 24", kicker: "PLACEMENT · CAREER · INDEPENDENCE",
    title: "Career & Independence",
    body: "Where the Last are first in line — paid placement with one of 250+ partner employers, a real paycheck, and a path to homeownership.",
    bullets: ["Direct placement with pre-certified employer roster", "Inline payroll + compliance via our Personal Advancement and Career Training", "Phase-III homeownership pathway for residents who make the run"],
    img: "https://images.unsplash.com/photo-1758691737246-95bf8f09a997?auto=format&fit=crop&w=1200&q=80",
    alt: "Office workers collaborating candidly around a shared screen.",
  },
];
const PhaseCards = () => (
  <>
    <h2 id="alliances-anchor" className="sr-only">Non-Profit Alliance Integration</h2>
    <section data-testid="nonprofits-breakdown" className="py-24 lg:py-28 border-t" style={{ background: C.bgCream, borderColor: `${C.ink}0D` }}>
      <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="mb-14 lg:mb-18 max-w-3xl">
          <div className="tea-mono text-[10px] tracking-[0.25em] uppercase font-bold mb-4" style={{ color: C.pine }}>
            <span className="inline-flex items-center gap-2"><span className="h-1.5 w-6" style={{ background: C.pine }} />Three Phases · One Mandate</span>
          </div>
          <h2 className="tea-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" style={{ color: C.ink }}>
            Each phase has a name, a standard, <span style={{ color: C.pine }}>and someone accountable.</span>
          </h2>
          <p className="mt-5 text-sm md:text-base leading-relaxed" style={{ color: `${C.ink}99` }}>
            We establish all three phases and hold the standard for each one. Credentialed partner agencies come alongside us to administer them — so the person the world left for last moves through one continuous journey instead of starting over at every door.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-10 lg:gap-14">
          {PHASES.map((p) => (
            <a key={p.k} href={p.href} data-testid={`nonprofit-card-${p.k}`}
              className="group block cursor-pointer grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center bg-white rounded-3xl p-4 md:p-6 lg:p-8 border shadow-sm hover:shadow-2xl transition-all duration-200 hover:-translate-y-1"
              style={{ borderColor: `${C.ink}0D`, textDecoration: "none" }}>
              <div className={`relative lg:col-span-5 aspect-[4/3] rounded-2xl overflow-hidden ${p.flip ? "lg:order-2" : ""}`} style={{ background: C.bgCreamAlt }}>
                <img alt={p.alt} src={p.img} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span data-testid={`nonprofit-phase-plate-${p.k}`} className="absolute top-3 left-3 px-3 py-1.5 rounded-full backdrop-blur border shadow-md tea-mono text-[10px] tracking-[0.22em] uppercase font-bold" style={{ background: "#FFFFFFF2", borderColor: `${C.ink}1A`, color: p.color }}>
                  {p.plate}
                </span>
              </div>
              <div className={`lg:col-span-7 flex flex-col gap-5 ${p.flip ? "lg:order-1 lg:pr-4" : "lg:pl-4"}`}>
                <div className="flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
                  <span className="tea-mono text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: p.color }}>{p.kicker}</span>
                </div>
                <h3 className="tea-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" style={{ color: C.ink }}>{p.title}</h3>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: `${C.ink}BF` }}>{p.body}</p>
                <ul className="flex flex-col gap-2.5 mt-1">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm md:text-base leading-relaxed" style={{ color: `${C.ink}B3` }}>
                      <span className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: p.color }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <span data-testid={`nonprofit-cta-${p.k}`} className="mt-3 inline-flex items-center gap-2 font-semibold text-sm tracking-tight group-hover:gap-3 transition-all" style={{ color: C.pine }}>
                  {p.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" style={{ borderColor: `${C.ink}1A` }}>
          <p className="text-sm leading-relaxed max-w-2xl" style={{ color: `${C.ink}99` }}>
            Chartered and operated by <strong style={{ color: C.ink }}>The Life Foundation</strong>, holder of the operating charter, the 159-unit residential community, and the fiduciary obligations to residents and funders.
          </p>
          <a href="https://lifefoundationrebuild.com/" target="_blank" rel="noopener noreferrer" data-testid="charter-holder-link" className="shrink-0 inline-flex items-center gap-2 font-semibold text-sm hover:gap-3 transition-all" style={{ color: C.pine }}>
            Visit The Life Foundation <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  </>
);

// ===================================================
// PACT — 6 TOOLS
// ===================================================
const UCMS_TOOLS = [
  ["smartphone_receipt_capture", Receipt, "Smartphone Receipt Capture", "Crews snap receipts; we OCR, classify, and route to your accounting in real time. Audit-trail attached, no spreadsheet sprawl."],
  ["unified_roster_management", ClipboardList, "Unified Roster Management", "A single source of truth across hiring, scheduling, and active-worker compliance. No more weekly sync calls."],
  ["compliance_dashboard", ShieldCheck, "Real-Time Compliance Dashboard", "Drug-test cadence, background-check renewals, certification expirations — surfaced before they become a liability."],
  ["direct_billable_hours", FileSpreadsheet, "Direct Billable Hours", "Time stamps from the field flow into invoices weekly. Reconciled hours, no disputes, predictable cash."],
  ["workforce_pre_certification", Award, "Workforce Pre-Certification", "Every candidate arrives with documented sobriety, training, and a TEA accountability sponsor. Start day one, not week three."],
  ["live_payroll_reporting", BarChart3, "Live Pay-Period Reporting", "Drill down by site, supervisor, or trade. Export to your AP system in a click. Quarterly close in hours, not weeks."],
];
const Ucms = () => (
  <>
    <h2 id="ucms-anchor" className="sr-only">Personal Advancement and Career Training (PACT)</h2>
    <section data-testid="employer-benefits" className="py-24 lg:py-28 border-t" style={{ background: C.bgCreamAlt, borderColor: `${C.ink}0D` }}>
      <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="mb-14 lg:mb-18 max-w-3xl">
          <div className="tea-mono text-[10px] tracking-[0.25em] uppercase font-bold mb-4" style={{ color: C.pine }}>
            <span className="inline-flex items-center gap-2"><span className="h-1.5 w-6" style={{ background: C.pine }} />For Our Employer Partners</span>
          </div>
          <h2 className="tea-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" style={{ color: C.ink }}>
            Six tools that turn TEA placements into <span style={{ color: C.pine }}>your most reliable hires.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: `${C.ink}B3` }}>
            Our Personal Advancement and Career Training (PACT) doesn't stop at placement — it keeps coordinating support through pay periods, compliance checks, and promotion windows, with less paperwork for your team.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {UCMS_TOOLS.map(([k, Icon, title, body]) => (
            <article key={k} data-testid={`employer-benefit-${k}`} className="bg-white rounded-2xl p-8 border hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-md" style={{ borderColor: `${C.ink}0D` }}>
              <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-5" style={{ background: `${C.gold}1A` }}>
                <Icon className="h-5 w-5" style={{ color: C.gold }} strokeWidth={2.2} />
              </div>
              <h3 className="tea-display text-xl font-bold tracking-tight mb-3" style={{ color: C.ink }}>{title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: `${C.ink}B3` }}>{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  </>
);

// ===================================================
// STRATEGIC PARTNERS
// ===================================================
const PARTNERS = [
  ["hilton-resorts", "Hospitality", "Hilton Resorts", "Lead Vocational Partner"],
  ["city-of-north-myrtle-beach", "Government", "City of North Myrtle Beach", "Municipal Partner"],
  ["horry-county-workforce", "Public Agency", "Horry County Workforce", "Workforce Pipeline"],
  ["nccer", "Trades Certification", "NCCER", "Certifying Body"],
  ["south-carolina-works", "State Agency", "South Carolina Works", "Funding Partner"],
  ["local-construction-guilds", "Trades Network", "Local Construction Guilds", "Trade Employers"],
];
const Partners = () => (
  <section id="partners" data-testid="partners-section" className="relative py-24 sm:py-28 border-b" style={{ background: C.bgCream, borderColor: C.rule }}>
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-7">
          <div className="tea-mono text-[11px] tracking-[0.28em] uppercase mb-4 flex items-center gap-3" style={{ color: C.goldDeep }}>
            <span className="h-px w-8" style={{ background: C.goldDeep }} /> §03 · Strategic Partners
          </div>
          <h2 className="tea-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98]" style={{ color: C.ink }}>
            Trusted by the partners<br />
            <span className="italic font-medium" style={{ color: C.mute }}>that put people to work.</span>
          </h2>
        </div>
        <div className="lg:col-span-4 lg:col-start-9 lg:pt-3">
          <p className="text-base leading-relaxed" style={{ color: `${C.ink}BF` }}>
            The Alliance only works because municipalities, employers, and certifying bodies stake their reputation alongside ours in serving the Last, the Least and the Lost.
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px border" style={{ background: C.rule, borderColor: C.rule }}>
        {PARTNERS.map(([slug, sector, name, role]) => (
          <div key={slug} data-testid={`partner-${slug}`} className="bg-white p-6 sm:p-7 hover:bg-[color:var(--hb,#FBF8EE)] transition-colors group" style={{ "--hb": "#FBF8EE" }}>
            <div className="flex items-start justify-between mb-4">
              <Building2 className="h-5 w-5" style={{ color: C.goldDeep }} strokeWidth={1.5} />
              <div className="tea-mono text-[9px] tracking-[0.22em] uppercase" style={{ color: C.mute }}>{sector}</div>
            </div>
            <h3 className="tea-display font-semibold text-xl tracking-tight mb-2" style={{ color: C.ink }}>{name}</h3>
            <div className="tea-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: C.goldDeep }}>{role}</div>
          </div>
        ))}
      </div>
      <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="tea-display italic text-lg" style={{ color: `${C.ink}B3` }}>Want to put your company's logo here?</div>
        <a href="#assist" data-testid="partners-cta" className="tea-mono text-[11px] tracking-[0.22em] uppercase border-b-2 pb-1 self-start" style={{ color: C.ink, borderColor: C.goldDeep }}>
          Become an Employer Partner →
        </a>
      </div>
    </div>
  </section>
);

// ===================================================
// ENGAGE / CONTACT FORM
// ===================================================
const INQ_TYPES = [
  { k: "assistance", icon: HeartHandshake, color: "#7c3aed", label: "Get Assistance", sub: "I or someone I know needs help." },
  { k: "partner", icon: Building2, color: C.goldDeep, label: "Partner With Us", sub: "We're a nonprofit or municipality." },
  { k: "donor", icon: HandCoins, color: "#1e40af", label: "Donate · Support", sub: "I want to fund the work financially." },
];
const EngageAlliance = () => {
  const [typ, setTyp] = useState("assistance");
  const activeColor = INQ_TYPES.find((t) => t.k === typ)?.color || C.goldDeep;
  return (
    <section id="assist" data-testid="assist-section" className="relative py-24 sm:py-32 border-b" style={{ background: "#fff", borderColor: C.rule }}>
      <div id="donate" className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="tea-mono text-[11px] tracking-[0.28em] uppercase mb-4 flex items-center gap-3" style={{ color: C.goldDeep }}>
              <span className="h-px w-8" style={{ background: C.goldDeep }} /> §04 · Engage the Alliance
            </div>
            <h2 className="tea-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98] mb-6" style={{ color: C.ink }}>Need help today?</h2>
            <p className="text-base leading-relaxed mb-10 max-w-md" style={{ color: `${C.ink}CC` }}>
              Tell us your situation. A Bridge Specialist from the Alliance will reach out within 48 hours — confidential, no obligation.
            </p>
            <div className="space-y-5">
              {[
                ["Resident Intake · Bridge Pipeline", "heather.a@hoh2life.com", "resident-intake"],
                ["Partnerships · Municipalities", "tea.info.help@hoh2life.com", "partnerships"],
                ["Employer Workforce Pipeline", "will.smith@hoh2life.com", "employer-pipeline"],
                ["Development · Donors", "andrew.m@hoh2life.com", "donors"],
              ].map(([lbl, mail, tid]) => (
                <div key={tid} className="pl-4 border-l-2" style={{ borderColor: C.goldDeep }}>
                  <div className="tea-mono text-[10px] tracking-[0.22em] uppercase mb-1" style={{ color: C.mute }}>{lbl}</div>
                  <a href={`mailto:${mail}`} data-testid={`contact-mail-${tid}`} className="tea-display text-base font-medium tracking-tight underline-offset-4 hover:underline break-all" style={{ color: C.ink }}>{mail}</a>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form data-testid="inquiry-form" onSubmit={(e) => e.preventDefault()} className="bg-white border p-8 sm:p-10 relative shadow-sm" style={{ borderColor: C.rule }}>
              <div className="absolute top-0 left-0 right-0 h-1 transition-colors" style={{ background: activeColor }} />
              <div className="mb-8">
                <label className="tea-mono text-[10px] tracking-[0.22em] uppercase mb-3 block" style={{ color: C.mute }}>I am reaching out as</label>
                <div className="grid sm:grid-cols-3 gap-3">
                  {INQ_TYPES.map(({ k, icon: Icon, color, label, sub }) => {
                    const on = k === typ;
                    return (
                      <button key={k} type="button" data-testid={`inquiry-type-${k}`} onClick={() => setTyp(k)}
                        className="p-4 text-left transition-all"
                        style={on
                          ? { background: C.bgCream, border: `1px solid ${C.ink}`, borderTop: `3px solid ${color}` }
                          : { background: "#fff", border: `1px solid ${C.rule}` }}>
                        <Icon className="h-4 w-4 mb-2" style={{ color }} strokeWidth={1.6} />
                        <div className="tea-mono text-[10px] tracking-[0.18em] uppercase font-semibold" style={{ color: C.ink }}>{label}</div>
                        <div className="text-[11px] mt-1" style={{ color: C.mute }}>{sub}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <Field id="name" label="Your Name (or referrer's)" />
                <Field id="email" label="Email" type="email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <Field id="organization" label="City / Location" />
                <Field id="role" label="Relationship" />
              </div>
              <div className="mb-8">
                <label htmlFor="message" className="tea-mono text-[10px] tracking-[0.22em] uppercase mb-2 block" style={{ color: C.mute }}>What's happening?</label>
                <textarea id="message" rows={5} data-testid="field-message"
                  placeholder="A few sentences about the situation — current housing, immediate needs, contact preference."
                  className="w-full border px-3 py-2 shadow-sm bg-[color:var(--fcr,#FAFAF7)] text-[color:var(--ink,#0A0612)] text-base resize-none focus:outline-none focus:border-[color:var(--ink,#0A0612)]"
                  style={{ "--fcr": C.bgCream, "--ink": C.ink, borderColor: C.rule, borderRadius: 0 }} />
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t" style={{ borderColor: C.rule }}>
                <div className="tea-mono text-[10px] tracking-[0.22em] uppercase max-w-xs" style={{ color: C.mute }}>All inquiries confidential · HIPAA scoped routing</div>
                <button type="submit" data-testid="inquiry-submit"
                  className="group inline-flex items-center justify-center gap-2 whitespace-nowrap tea-mono text-xs tracking-[0.2em] uppercase h-12 px-6 ring-1"
                  style={{ background: C.ink, color: C.goldLight, borderRadius: 0, boxShadow: "0 1px 2px rgba(0,0,0,.08)", "--rc": `${C.gold}4D`, outlineColor: `${C.gold}4D` }}>
                  Submit Securely <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
const Field = ({ id, label, type = "text" }) => (
  <div>
    <label htmlFor={id} className="tea-mono text-[10px] tracking-[0.22em] uppercase mb-2 block" style={{ color: C.mute }}>{label}</label>
    <input id={id} type={type} data-testid={`field-${id}`} required
      className="w-full border px-3 py-1 shadow-sm bg-[color:var(--fcr,#FAFAF7)] text-base h-11 focus:outline-none focus:border-[color:var(--ink,#0A0612)]"
      style={{ "--fcr": C.bgCream, "--ink": C.ink, borderColor: C.rule, color: C.ink, borderRadius: 0 }} />
  </div>
);

// ===================================================
// FOOTER
// ===================================================
const Footer = () => (
  <footer data-testid="tea-footer" className="text-white" style={{ background: C.ink }}>
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
      <div className="md:col-span-2">
        <div className="flex items-center gap-3 mb-4">
          <img src="/tea-mark.png" alt="" className="h-10 w-10 object-contain" />
          <div className="tea-display font-black text-lg tracking-tight"><TeaWordmark text="THE EXCHANGE ALLIANCE" testid="home-footer-wordmark" /></div>
        </div>
        <p className="text-sm text-white/70 leading-relaxed max-w-md">
          Serving the Last, the Least and the Lost. A Personal Advancement and Career Training moving residents from crisis to self-sufficiency — operated by The Life Foundation.
        </p>
      </div>
      <div>
        <div className="tea-mono text-[10px] tracking-[0.22em] uppercase mb-3" style={{ color: C.goldLight }}>Program</div>
        <ul className="space-y-2 text-sm text-white/70">
          <li><a href="#alliances-anchor" className="hover:text-white">Three Phases</a></li>
          <li><a href="#four-pillars" className="hover:text-white">4 Pillars</a></li>
          <li><a href="#roadmap" className="hover:text-white">24-Month Roadmap</a></li>
          <li><a href="#impact" className="hover:text-white">Live Impact</a></li>
        </ul>
      </div>
      <div>
        <div className="tea-mono text-[10px] tracking-[0.22em] uppercase mb-3" style={{ color: C.goldLight }}>Engage</div>
        <ul className="space-y-2 text-sm text-white/70">
          <li><a href="#assist" className="hover:text-white">Get Assistance</a></li>
          <li><a href="#assist" className="hover:text-white">Partner With Us</a></li>
          <li><a href="#assist" className="hover:text-white">Donate</a></li>
          <li><a href="#commercial" data-testid="footer-demo-link" className="hover:text-white">Watch the commercial</a></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10 py-6 text-center tea-mono text-[10px] tracking-[0.22em] uppercase text-white/50">
      © {new Date().getFullYear()} The Life Foundation · The Exchange Alliance™
      <div data-testid="footer-tech-credit" className="mt-2 normal-case tracking-[0.12em] text-white/35">Technology by Next Engines</div>
    </div>
  </footer>
);

// ===================================================
// EXPORT
// ===================================================
export default function HomePage() {
  return (
    <main data-testid="landing-root" className="tea-root" style={{ background: C.bgCream, color: C.ink }}>
      <style>{PAGE_CSS}</style>
      <h1 className="sr-only">The Exchange Alliance — Serving the Last, the Least and the Lost: HIS Mission, HIS Mandate</h1>
      <Header />
      <Hero />
      <ImpactTicker />
      <LiveDataCta />
      <EntryCta />
      <RoadmapPreview />
      <CareerDestinations />
      <PhaseCards />
      <Ucms />
      <Partners />
      <EngageAlliance />
      <Footer />
    </main>
  );
}
